import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { artistApi } from '../../api/artist';
import { discoveryApi } from '../../api/discovery';
import Link from '../../features/browse/BrowseLink';
import ArtistContentCard from '../../features/browse/ArtistContentCard';
import {
  artistIdentity,
  artistSections,
  artistViewPath,
  artistSectionTracking,
} from '../../features/browse/artist';
import { BrowseInlineAd } from '../../features/browse/BrowseSponsored';
import { useSponsoredPlacements } from '../../features/ads/useSponsoredPlacements';
import ContentShelf, { ShelfNavigationContext } from '../../components/responsive/ContentShelf';
import Chevron from '../../components/content/Chevron';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import SEO from '../../components/common/SEO';
import OpenInAppBanner from '../../components/common/OpenInAppBanner';
import useTrackPageView from '../../hooks/useTrackPageView';
import { PAGE_TYPE, CONTENT_TYPE, ARTIST_ACTION_TYPE } from '../../constants/gaEnums';
import { trackArtistContentClick } from '../../lib/gaEvents';
import { useUserStore } from '../../stores/userStore';
import ArtistShell from './ArtistShell';
import RecoverableImage from '../../features/images/RecoverableImage';
import { useArtistPageMemory, useArtistRailMemory } from './useArtistMemory';
export function ArtistQueryState({ query, children }) {
  const { locale } = useWebLocale(),
    en = locale === 'en';
  if (query.isPending) return <p role="status">{en ? 'Loading…' : '불러오는 중…'}</p>;
  return (
    <>
      {query.isError && (
        <div className="pp-artist-error" role="alert">
          <span>{en ? 'Could not load this content.' : '콘텐츠를 불러오지 못했어요.'}</span>
          <button className="pp-action" onClick={() => query.refetch()}>
            {en ? 'Try Again' : '다시 시도'}
          </button>
        </div>
      )}
      {query.data && children}
    </>
  );
}
export function ArtistHero({ artist }) {
  const { locale } = useWebLocale(),
    en = locale === 'en',
    [failed, setFailed] = useState(false),
    image = artist.imageUrl && !failed;
  return (
    <div className={`pp-artist-hero${image ? ' has-image' : ''}`}>
      {image && (
        <span className="pp-artist-backdrop" aria-hidden="true">
          <RecoverableImage src={artist.imageUrl} alt="" />
        </span>
      )}
      <span className="pp-artist-portrait">
        {artist.imageUrl ? (
          <RecoverableImage
            src={artist.imageUrl}
            alt=""
            onFailureChange={setFailed}
            fallback={<span>postpop</span>}
          />
        ) : (
          <span>postpop</span>
        )}
      </span>
      <h1>{artist.name}</h1>
      {['KR', 'GLOBAL'].includes(artist.originRegion) && (
        <span className="pp-artist-region">
          {artist.originRegion === 'KR' ? (en ? 'Korea' : '국내') : en ? 'International' : '해외'}
        </span>
      )}
    </div>
  );
}
export function ArtistSection({ definition, bucket, query, artist }) {
  const { locale } = useWebLocale(),
    en = locale === 'en',
    user = useUserStore((s) => s.user);
  const [key, view, ko, english] = definition,
    title = en ? english : ko,
    total = bucket?.total || 0;
  const kind =
    key === 'includedPlaylists' ? 'playlist' : key === 'similarArtists' ? 'artist' : 'album';
  const [wide, setWide] = useState(() => window.innerWidth >= 1280);
  useEffect(() => {
    const resize = () => setWide(window.innerWidth >= 1280);
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);
  const preview = bucket?.items || [];
  // The detail API returns four album previews. Fill a desktop rail from the
  // existing read-only full view; keep the mobile preview and true total intact.
  const needsMore = wide && kind === 'album' && total > preview.length && preview.length < 6;
  const extra = useQuery({
    queryKey: ['artist-wide-preview', artist.artistSource, artist.sourceId, view],
    queryFn: ({ signal }) =>
      artistApi.getArtistViewByType(artist.artistSource, artist.sourceId, view, {
        signal,
        suppressGlobalError: true,
      }),
    enabled: needsMore && Boolean(artist.sourceId),
    retry: false,
    staleTime: 120000,
  });
  const items = (
    needsMore && extra.data?.data?.items?.length > preview.length ? extra.data.data.items : preview
  ).slice(0, 8);
  const ref = useArtistRailMemory(key, items.length);
  if (!items.length && !query?.isPending && !query?.isError) return null;
  const heading = (
    <>
      {title}
      {total > 0 && <small>{total}</small>}
    </>
  );
  return (
    <section ref={ref}>
      <div className="pp-artist-section-heading">
        <h2>
          {total > items.length ? (
            <Link
              aria-label={en ? `View all ${title}` : `${title} 전체 보기`}
              to={artistViewPath(artist, view)}
              onClick={() =>
                trackArtistContentClick(
                  {
                    artistSection: artistSectionTracking[view],
                    actionType: ARTIST_ACTION_TYPE.VIEW_ALL,
                    pageType: PAGE_TYPE.ARTIST,
                  },
                  user,
                )
              }
            >
              {heading}
              <Chevron />
            </Link>
          ) : (
            heading
          )}
        </h2>
      </div>
      {query?.isPending && !items.length && <p role="status">{en ? 'Loading…' : '불러오는 중…'}</p>}
      {(query?.isError || (needsMore && extra.isError)) && (
        <div className="pp-artist-error" role="alert">
          <span>{en ? 'Could not load this section.' : '이 영역을 불러오지 못했어요.'}</span>
          <button
            className="pp-action"
            onClick={() => (needsMore && extra.isError ? extra.refetch() : query.refetch())}
          >
            {en ? 'Try Again' : '다시 시도'}
          </button>
        </div>
      )}
      <ContentShelf
        profile={kind === 'artist' ? 'people' : 'albums'}
        className="pp-artist-shelf"
        label={title}
      >
        {items.map((item, index) => (
          <ArtistContentCard
            key={`${item.artistSource || item.albumSource || ''}:${item.sourceId || item.id}-${index}`}
            item={item}
            kind={kind}
            section={view}
            focusKey={`${view}:${index}`}
          />
        ))}
      </ContentShelf>
    </section>
  );
}
export default function ArtistDetail() {
  const params = useParams(),
    { state } = useLocation(),
    [search] = useSearchParams(),
    identity = artistIdentity(params, search, state),
    user = useUserStore((s) => s.user);
  const query = useQuery({
    queryKey: ['browse-artist', identity.slug, identity.sourceId, identity.sourceType],
    queryFn: ({ signal }) =>
      identity.slug
        ? artistApi.getArtistBySlug(
            identity.slug,
            {
              sourceId: identity.sourceId,
              sourceType: identity.sourceType,
            },
            { signal, suppressGlobalError: true },
          )
        : artistApi.getArtistById(identity.sourceType, identity.sourceId, {
            signal,
            suppressGlobalError: true,
          }),
    staleTime: 120000,
  });
  const data = query.data?.data,
    artist = data?.artist;
  const included = useQuery({
    queryKey: ['browse-artist-playlists', artist?.sourceId, artist?.artistSource],
    queryFn: ({ signal }) =>
      artistApi.getArtistViewByType(artist.artistSource, artist.sourceId, 'included-playlists', {
        signal,
        suppressGlobalError: true,
      }),
    enabled: Boolean(artist?.sourceId),
    staleTime: 120000,
  });
  const similar = useQuery({
    queryKey: ['browse-artist-similar', artist?.sourceId, artist?.artistSource],
    queryFn: ({ signal }) =>
      artistApi.getArtistViewByType(artist.artistSource, artist.sourceId, 'similar-artists', {
        signal,
        suppressGlobalError: true,
      }),
    enabled: Boolean(artist?.sourceId),
    staleTime: 120000,
  });
  const campaigns = useSponsoredPlacements('ARTIST_DETAIL');
  const remember = useArtistPageMemory(
    !query.isPending && (!artist || (!included.isPending && !similar.isPending)),
  );
  useEffect(() => {
    if (!user?.id || !artist?.id) return;
    const controller = new AbortController();
    void discoveryApi
      .recordEvents(
        [{ eventType: 'ARTIST_OPEN', artistId: artist.id, context: 'artist_detail' }],
        controller.signal,
      )
      .catch(() => {});
    return () => controller.abort();
  }, [user?.id, artist?.id]);
  useTrackPageView(
    {
      pageType: PAGE_TYPE.ARTIST,
      contentType: CONTENT_TYPE.ARTIST,
      contentId: artist?.sourceId,
      contentName: artist?.name,
    },
    { ready: Boolean(artist) },
  );
  return (
    <ArtistShell artist={artist} position={remember}>
      <ArtistQueryState query={query}>
        {artist && (
          <>
            <SEO title={artist.name} image={artist.imageUrl} />
            <OpenInAppBanner type="artist" title={artist.name} image={artist.imageUrl} />
            <ArtistHero key={artist.imageUrl || artist.sourceId} artist={artist} />
            <ShelfNavigationContext.Provider value="edges">
              <div className="pp-artist-sections">
                {artistSections.map((def) => {
                  const key = def[0],
                    request =
                      key === 'includedPlaylists'
                        ? included
                        : key === 'similarArtists'
                          ? similar
                          : null;
                  const bucket =
                    request?.data?.data || (key === 'includedPlaylists' ? undefined : data[key]);
                  return (
                    <React.Fragment key={key}>
                      {key === 'similarArtists' &&
                        campaigns.map((c) => <BrowseInlineAd key={c.id} campaign={c} />)}
                      <ArtistSection
                        definition={def}
                        bucket={bucket}
                        query={request}
                        artist={artist}
                      />
                    </React.Fragment>
                  );
                })}
              </div>
            </ShelfNavigationContext.Provider>
          </>
        )}
      </ArtistQueryState>
    </ArtistShell>
  );
}
